// src/hooks/useSyncManager.ts
// 統一的同步管理 Hook，用於在恢復連線時自動同步所有待同步項目

import { useEffect, useRef, useCallback, useState } from 'react';
import { processSyncQueue } from '../services/syncQueueService';
import { logInfo, logWarn } from '../utils/logger';
import { eventBus } from '../utils/eventBus';

/**
 * 統一的同步管理 Hook
 *
 * 職責：
 * - 監聽網路狀態變化
 * - 在恢復連線時自動執行同步佇列
 * - 防止重複同步
 * - 發布同步完成事件供其他組件監聽
 */
export const useSyncManager = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const lastSyncTimeRef = useRef<number>(0);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 執行自動同步
   */
  const performAutoSync = useCallback(async () => {
    // 防止重複同步（3秒內不重複執行）
    const now = Date.now();
    if (isSyncing || now - lastSyncTimeRef.current < 3000) {
      logInfo('Sync', '跳過重複同步');
      return;
    }

    setIsSyncing(true);
    logInfo('Sync', '開始自動同步佇列');

    try {
      const result = await processSyncQueue();

      if (result.success > 0) {
        logInfo('Sync', `同步成功: ${result.success} 個項目`);
        // 發布同步完成事件，讓其他組件可以刷新資料
        eventBus.publish('syncCompleted', { success: result.success, failed: result.failed });
      }

      if (result.failed > 0) {
        logWarn('Sync', `同步失敗: ${result.failed} 個項目`);
      }

      lastSyncTimeRef.current = Date.now();

    } catch (error) {
      logWarn('Sync', '自動同步失敗', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  /**
   * 監聽網路狀態變化
   */
  useEffect(() => {
    const handleOnline = () => {
      logInfo('Network', '網路已連線，準備同步');

      // 清除可能存在的延遲任務
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // 延遲 2 秒後執行同步，確保網路穩定
      syncTimeoutRef.current = setTimeout(() => {
        performAutoSync();
      }, 2000);
    };

    const handleOffline = () => {
      logInfo('Network', '網路已離線');

      // 清除可能存在的同步任務
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 初始化時，如果已經在線，檢查是否有待同步項目
    if (navigator.onLine) {
      syncTimeoutRef.current = setTimeout(() => {
        performAutoSync();
      }, 3000); // 應用啟動 3 秒後檢查
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [performAutoSync]);

  return {
    isSyncing,
    performAutoSync
  };
};
